import React, { useEffect, useState } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import { Input, Select } from "../../components/ui";
import {
  listProjectInvitations,
  createProjectInvitations,
  regenerateInvitation,
  copyTextToClipboard,
  type ProjectInvitationListItem,
  type CreateInvitationResult,
  type InvitationRoleName,
} from "../../services/invitationApi";
import ContentLoader from "../../components/ContentLoader";

interface ProjectInvitationsPanelProps {
  projectId: number;
  projectName: string;
  /** Invitations require a published assessment on the project. */
  assessmentBlueprintId?: number | null;
  show: boolean;
  onHide: () => void;
}

interface GeneratedInviteLink {
  emailAddress?: string;
  inviteUrl: string;
  emailSent?: boolean;
  emailWarning?: string;
}

const invitationStatusClass = (status: string) => {
  switch (status) {
    case "Pending":
      return "app-status app-status--warning";
    case "Consumed":
      return "app-status app-status--success";
    case "Expired":
      return "app-status app-status--muted";
    case "Revoked":
      return "app-status app-status--danger";
    default:
      return "app-status app-status--muted";
  }
};

const roleLabel = (roleName?: string) => {
  if (roleName === "SuperUser") return "SuperUser (PM)";
  if (roleName === "Client") return "Client";
  return roleName || "Client";
};

const parseEmailList = (value: string): string[] => {
  const seen = new Set<string>();
  const emails: string[] = [];

  for (const part of value.split(",")) {
    const email = part.trim().toLowerCase();
    if (!email || seen.has(email)) continue;
    seen.add(email);
    emails.push(part.trim());
  }

  return emails;
};

const isLikelyEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const ProjectInvitationsPanel: React.FC<ProjectInvitationsPanelProps> = ({
  projectId,
  projectName,
  assessmentBlueprintId,
  show,
  onHide,
}) => {
  const hasAssessmentBlueprint = Boolean(
    assessmentBlueprintId && assessmentBlueprintId > 0
  );
  const [invitations, setInvitations] = useState<ProjectInvitationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [email, setEmail] = useState("");
  const [roleName, setRoleName] = useState<InvitationRoleName>("Client");
  const [expiryPolicy, setExpiryPolicy] = useState<"SevenDays" | "ThirtyDays" | "Never">("SevenDays");
  const [generatedLinks, setGeneratedLinks] = useState<GeneratedInviteLink[]>([]);
  const [skippedMessages, setSkippedMessages] = useState<string[]>([]);
  const [regeneratingId, setRegeneratingId] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingEmailCount, setGeneratingEmailCount] = useState(0);

  const isBusy = isGenerating || regeneratingId !== null;
  const isSuperUserInvite = roleName === "SuperUser";
  const generateLoaderLabel =
    generatingEmailCount > 1
      ? "Generating invite and sending email invites…"
      : generatingEmailCount === 1
        ? "Generating invite and sending email invite…"
        : "Generating invite…";

  const loadInvitations = async () => {
    setLoading(true);
    setError("");
    try {
      const items = await listProjectInvitations(projectId);
      setInvitations(items);
    } catch (loadError) {
      setError(
        loadError instanceof Error ? loadError.message : "Failed to load invitations."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && projectId) {
      loadInvitations();
    }
  }, [show, projectId]);

  const handleClose = () => {
    if (isBusy) return;
    onHide();
  };

  const handleGenerate = async () => {
    if (isBusy) return;
    if (!hasAssessmentBlueprint) {
      setError(
        "Publish an assessment to this project before inviting clients or Super Users."
      );
      return;
    }
    setError("");
    setSuccess("");
    setSkippedMessages([]);

    const emails = parseEmailList(email);

    if (isSuperUserInvite && emails.length === 0) {
      setError("Email is required when inviting a SuperUser (project manager).");
      return;
    }

    if (emails.length > 0) {
      const invalid = emails.filter((item) => !isLikelyEmail(item));
      if (invalid.length > 0) {
        setError(`Invalid email address(es): ${invalid.join(", ")}`);
        return;
      }
    }

    setIsGenerating(true);
    setGeneratingEmailCount(emails.length);
    try {
      const results: CreateInvitationResult[] = await createProjectInvitations({
        projectId,
        emailAddresses: emails.length > 0 ? emails : undefined,
        expiryPolicy,
        sendEmail: emails.length > 0,
        roleName,
      });

      const created = results.filter((result) => Boolean(result.inviteUrl));
      const skipped = results.filter(
        (result) => !result.inviteUrl && Boolean(result.emailWarning)
      );

      setGeneratedLinks(
        created.map((result) => ({
          emailAddress: result.emailAddress,
          inviteUrl: result.inviteUrl,
          emailSent: result.emailSent,
          emailWarning: result.emailWarning,
        }))
      );
      setSkippedMessages(skipped.map((result) => result.emailWarning!).filter(Boolean));

      const roleText = isSuperUserInvite ? "SuperUser" : "Client";
      if (created.length === 0 && skipped.length > 0) {
        setError(skipped.map((result) => result.emailWarning).join(" "));
      } else if (created.length === 1) {
        const only = created[0];
        if (only.emailSent) {
          setSuccess(`${roleText} invitation generated and email sent successfully.`);
        } else if (only.emailWarning) {
          setSuccess(
            `${roleText} invitation created. Copy the URL below — email was not sent: ${only.emailWarning}`
          );
        } else {
          setSuccess(
            `${roleText} invitation generated. Copy the URL below — it is only shown once.`
          );
        }
      } else if (created.length > 1) {
        const emailed = created.filter((result) => result.emailSent).length;
        setSuccess(
          `${created.length} ${roleText} invitations generated` +
            (emailed > 0 ? ` (${emailed} email${emailed === 1 ? "" : "s"} sent)` : "") +
            ". Copy each URL below — they are only shown once."
        );
      }

      setEmail("");
      await loadInvitations();
    } catch (generateError) {
      setError(
        generateError instanceof Error ? generateError.message : "Failed to generate invitation."
      );
    } finally {
      setIsGenerating(false);
      setGeneratingEmailCount(0);
    }
  };

  const handleCopy = async (url: string) => {
    await copyTextToClipboard(url);
    setSuccess("Invitation URL copied to clipboard.");
  };

  const handleCopyAll = async () => {
    const text = generatedLinks
      .map((link) =>
        link.emailAddress ? `${link.emailAddress}\t${link.inviteUrl}` : link.inviteUrl
      )
      .join("\n");
    await copyTextToClipboard(text);
    setSuccess("All invitation URLs copied to clipboard.");
  };

  const handleRegenerate = async (
    invitationId: number,
    invitationEmail?: string | null
  ) => {
    if (isBusy) return;
    setRegeneratingId(invitationId);
    setError("");
    setSuccess("");
    setSkippedMessages([]);
    try {
      const shouldSendEmail = Boolean(invitationEmail?.trim());
      const result = await regenerateInvitation(invitationId, {
        expiryPolicy,
        sendEmail: shouldSendEmail,
      });
      setGeneratedLinks([
        {
          emailAddress: result.emailAddress,
          inviteUrl: result.inviteUrl,
          emailSent: result.emailSent,
          emailWarning: result.emailWarning,
        },
      ]);
      if (result.emailSent) {
        setSuccess("Invitation regenerated and email sent.");
      } else if (result.emailWarning) {
        setSuccess(
          `Invitation regenerated, but email failed: ${result.emailWarning}. Copy the new URL below.`
        );
      } else {
        setSuccess("Invitation regenerated. Copy the new URL below.");
      }
      await loadInvitations();
    } catch (regenerateError) {
      setError(
        regenerateError instanceof Error
          ? regenerateError.message
          : "Failed to regenerate invitation."
      );
    } finally {
      setRegeneratingId(null);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
      enforceFocus={false}
      restoreFocus
      dialogClassName="pm-invitations-dialog rounded-3"
      contentClassName="pm-invitations-modal"
    >
      <Modal.Header closeButton={!isBusy} className="dashboard-modal-header">
        <Modal.Title>Manage user invitations — {projectName}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pm-invitations-panel">
        {!hasAssessmentBlueprint && (
          <div className="alert alert-warning mb-3">
            Publish an assessment to this project before inviting clients or Super Users.
          </div>
        )}

        {(error || success || skippedMessages.length > 0) && (
          <div className="pm-invitations-panel__alerts">
            {error && <div className="alert alert-danger mb-0">{error}</div>}
            {success && <div className="alert alert-success mb-0">{success}</div>}
            {skippedMessages.length > 0 && !error && (
              <div className="alert alert-warning mb-0">
                {skippedMessages.map((message) => (
                  <div key={message}>{message}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="pm-invitations-panel__form-section">
          <div className="cap-mgmt-section-label">Send invitation</div>
          <form
            className="pm-invitations-panel__form"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void handleGenerate();
            }}
          >
            <div className="pm-invitations-panel__fields">
              <Select
                label="Invite as"
                value={roleName}
                onChange={(value) => setRoleName(value as InvitationRoleName)}
                disabled={isBusy || !hasAssessmentBlueprint}
                options={[
                  { value: "Client", label: "Client" },
                  { value: "SuperUser", label: "SuperUser (project manager)" },
                ]}
              />
              <Input
                label="Email(s)"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={
                  isSuperUserInvite
                    ? "pm1@company.com, pm2@company.com"
                    : "client1@company.com, client2@company.com"
                }
                disabled={isBusy || !hasAssessmentBlueprint}
                required={isSuperUserInvite}
              />
              <Select
                label="Expiration"
                value={expiryPolicy}
                onChange={(value) =>
                  setExpiryPolicy(value as "SevenDays" | "ThirtyDays" | "Never")
                }
                disabled={isBusy || !hasAssessmentBlueprint}
                options={[
                  { value: "SevenDays", label: "7 days" },
                  { value: "ThirtyDays", label: "30 days" },
                  { value: "Never", label: "Never" },
                ]}
              />
            </div>
            <p className="pm-invitations-panel__hint">
              {isSuperUserInvite
                ? "Email is required. Separate multiple addresses with commas."
                : "Optional. Separate multiple addresses with commas, or leave empty for an open invite."}
            </p>
            <div className="pm-invitations-panel__form-actions">
              <Button
                type="submit"
                className="btn btn-dashboard-primary"
                disabled={isBusy || !hasAssessmentBlueprint}
              >
                {isGenerating ? "Generating…" : "Generate invitation"}
              </Button>
            </div>
          </form>

          {isGenerating && (
            <div className="pm-invitations-panel__generate-loader">
              <ContentLoader compact label={generateLoaderLabel} />
            </div>
          )}

          {!isGenerating && generatedLinks.length > 0 && (
            <div className="pm-invitation-url-box">
              <div className="pm-invitation-url-box__label">
                {generatedLinks.length === 1
                  ? "Latest invitation URL"
                  : `Generated invitation URLs (${generatedLinks.length})`}
              </div>
              {generatedLinks.length > 1 && (
                <Button
                  type="button"
                  size="sm"
                  className="btn btn-sm btn-dashboard-cancel mb-2"
                  onClick={() => void handleCopyAll()}
                >
                  Copy all
                </Button>
              )}
              {generatedLinks.map((link) => (
                <div key={`${link.emailAddress ?? "open"}-${link.inviteUrl}`} className="mb-2">
                  {link.emailAddress && (
                    <div className="pm-invitation-url-box__label">{link.emailAddress}</div>
                  )}
                  <code className="pm-invitation-url-box__code">{link.inviteUrl}</code>
                  <Button
                    type="button"
                    size="sm"
                    className="btn btn-sm btn-dashboard-cancel"
                    onClick={() => void handleCopy(link.inviteUrl)}
                  >
                    Copy URL
                  </Button>
                  {link.emailWarning && (
                    <div className="text-warning small mt-1">{link.emailWarning}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pm-invitations-panel__list-section">
          <div className="cap-mgmt-section-label">Invitation history</div>
          {loading ? (
            <ContentLoader compact label="Loading invitations…" />
          ) : (
            <div className="pm-invitations-table-wrap">
              <Table striped responsive size="sm" className="pm-invitations-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th className="pm-invitations-table__status-col">Status</th>
                    <th>Expires</th>
                    <th>Created</th>
                    <th className="pm-invitations-table__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="pm-invitations-table__empty">
                        No invitations yet.
                      </td>
                    </tr>
                  ) : (
                    invitations.map((invitation) => (
                      <tr key={invitation.invitationId}>
                        <td>{invitation.emailAddress || "Open invite"}</td>
                        <td>{roleLabel(invitation.roleName)}</td>
                        <td className="pm-invitations-table__status">
                          <span className={invitationStatusClass(invitation.status)}>
                            {invitation.status}
                          </span>
                        </td>
                        <td>
                          {invitation.expiresAt
                            ? new Date(invitation.expiresAt).toLocaleDateString()
                            : "Never"}
                        </td>
                        <td>{new Date(invitation.createdAt).toLocaleDateString()}</td>
                        <td className="pm-invitations-table__actions">
                          {invitation.status === "Pending" || invitation.status === "Expired" ? (
                            <Button
                              type="button"
                              size="sm"
                              className="btn btn-sm btn-dashboard-cancel"
                              disabled={isBusy}
                              onClick={() =>
                                handleRegenerate(
                                  invitation.invitationId,
                                  invitation.emailAddress
                                )
                              }
                            >
                              {regeneratingId === invitation.invitationId
                                ? "Regenerating…"
                                : "Regenerate"}
                            </Button>
                          ) : (
                            <span className="pm-invitations-table__muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="outline-secondary" onClick={handleClose} disabled={isBusy}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProjectInvitationsPanel;
